import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Users, DollarSign, MapPin, ShieldCheck, Trophy, Star, ChevronDown, Megaphone, Shirt } from "lucide-react";
import SectionHeading from "../components/SectionHeading";

const WARRIOR_IMG = "/assets/warrior-static.png";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden bg-[#F5EFE0]" data-testid="hero-section">
        <img src="/assets/titans-c-logo.jpg" alt="" aria-hidden className="absolute -left-32 -bottom-32 w-[600px] opacity-[0.06] pointer-events-none select-none" />
        <div className="absolute inset-0 grain-overlay" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#2D5F3F] bg-[#2D5F3F]/10 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#2D5F3F] animate-pulse" />
              <span className="font-display text-xs uppercase tracking-[0.3em] text-[#2D5F3F] font-bold">Mount Baldy Conference · 2026 Season</span>
            </div>
            <h1 className="font-display uppercase leading-[0.85] text-[#0A0A0A]">
              <span className="block text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-[0.15em] text-[#0A0A0A]/80">
                Hemet Pop Warner
              </span>
              <span className="block mt-2 text-6xl sm:text-7xl lg:text-9xl font-black text-[#2D5F3F]">
                Titans
              </span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-black/75 max-w-xl leading-relaxed">
              Youth tackle football for ages <span className="text-[#2D5F3F] font-bold">6 – 13</span>.
              Built on hard work, discipline, and the courage to compete.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/register"
                data-testid="hero-cta-register"
                className="group inline-flex items-center gap-2 bg-[#2D5F3F] hover:bg-[#1B4332] text-[#FDFBF7] font-display uppercase tracking-wider font-extrabold px-8 py-4 transition-all shadow-ink-offset hover:shadow-none hover:translate-x-1 hover:translate-y-1"
              >
                Secure Your Spot <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-coach"
                className="inline-flex items-center gap-2 border-2 border-[#2D5F3F] text-[#2D5F3F] hover:bg-[#2D5F3F] hover:text-[#FDFBF7] font-display uppercase tracking-wider font-bold px-8 py-4 transition-colors"
              >
                Coach &amp; Board Spots Open
              </Link>
            </div>

            {/* Quick stats */}
            <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl">
              {[
                { label: "Ages", value: "6–13" },
                { label: "All-In", value: "$100" },
                { label: "Conference", value: "Mt Baldy" },
                { label: "Starts", value: "Jul 20" },
              ].map((s) => (
                <div key={s.label} className="border-l-2 border-[#2D5F3F] pl-4">
                  <div className="font-display text-2xl sm:text-3xl font-black text-[#0A0A0A]">{s.value}</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-black/65 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* 3D Warrior + Helmet companion */}
          <div className="lg:col-span-5 flex justify-center items-center relative">
            {/* Floating helmet — sits to the right of the spinning warrior with breathing room */}
            <img
              src="/assets/titans-helmet.png"
              alt="Hemet Pop Warner Titans helmet"
              data-testid="hero-helmet"
              className="helmet-float pointer-events-none select-none absolute z-20 -bottom-16 sm:-bottom-20 lg:-bottom-24 right-0 sm:-right-2 lg:-right-16 w-[110px] sm:w-[150px] md:w-[170px] lg:w-[210px] drop-shadow-[0_20px_25px_rgba(0,0,0,0.35)]"
              draggable="false"
            />
            <div
              className="warrior-stage relative w-[300px] sm:w-[380px] md:w-[460px] lg:w-[520px] aspect-square"
              data-testid="hero-warrior-3d"
            >
              <div className="warrior-glow" />
              <div className="warrior-orbit">
                {Array.from({ length: 21 }).map((_, i) => {
                  const z = (i - 10) * 2.4; // -24px to +24px depth
                  const t = Math.abs(i - 10) / 10; // 0 center → 1 edge
                  const bright = 0.55 + (1 - t) * 0.45;
                  return (
                    <img
                      key={i}
                      src={WARRIOR_IMG}
                      alt={i === 10 ? "Cawston Titans Warrior" : ""}
                      aria-hidden={i !== 10}
                      className="warrior-slice"
                      style={{
                        transform: `translateZ(${z}px)`,
                        filter: `brightness(${bright})`,
                        opacity: i === 10 ? 1 : 0.92,
                      }}
                      draggable="false"
                    />
                  );
                })}
                <div className="warrior-lightsweep" />
              </div>
              <div className="warrior-floor" />
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-black/40 animate-bounce">
          <ChevronDown className="w-6 h-6" />
        </div>
      </section>

      {/* CALL FOR COACHES / BOARD */}
      <section className="bg-[#2D5F3F] py-12 text-[#FDFBF7]" data-testid="recruit-band">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <Megaphone className="w-8 h-8 shrink-0" />
            <div>
              <div className="font-display uppercase tracking-[0.3em] text-xs font-bold opacity-80">Now Recruiting</div>
              <h2 className="font-display text-2xl md:text-3xl font-black uppercase leading-tight">Coach &amp; Board Member Spots Are Open</h2>
            </div>
          </div>
          <a
            href="mailto:Cawstontitans@gmail.com"
            data-testid="band-email-link"
            className="inline-flex items-center gap-2 bg-[#FDFBF7] hover:bg-[#0A0A0A] text-[#2D5F3F] hover:text-[#FDFBF7] font-display uppercase tracking-wider font-extrabold px-6 py-3 transition-colors"
          >
            Cawstontitans@gmail.com <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* MISSION */}
      <section className="bg-[#F5EFE0] py-24 md:py-32 relative overflow-hidden" data-testid="mission-section">
        <img src="/assets/titans-c-logo.jpg" alt="" aria-hidden className="hidden md:block absolute -right-24 top-1/2 -translate-y-1/2 w-[480px] opacity-[0.07] pointer-events-none select-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 relative">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Our Mission" title="More Than A Game" />
            <div className="flex items-center gap-3 text-[#2D5F3F]">
              <Trophy className="w-5 h-5 text-[#2D5F3F]" />
              <span className="font-display uppercase tracking-widest text-sm font-bold">Character. Competition. Community.</span>
            </div>
          </div>
          <div className="lg:col-span-7 space-y-6 text-black/80 leading-relaxed text-base sm:text-lg">
            <p>
              Cawston Titans Youth Sports Organization is committed to fostering a safe, positive, and empowering
              environment where young athletes can develop their skills in football, cheer, basketball, and track &amp;
              field. Through structured coaching and mentorship, we instill values that extend far beyond the
              game—principles of <span className="text-[#2D5F3F] font-bold">hard work, dedication, integrity, and teamwork</span>.
            </p>
            <p>
              We believe that athletic excellence and character development go hand in hand. By emphasizing scholastic
              achievement, citizenship, and leadership, we prepare our participants for success both on and off the
              field.
            </p>
            <Link
              to="/about"
              data-testid="mission-read-more"
              className="inline-flex items-center gap-2 text-[#2D5F3F] font-display uppercase tracking-wider font-bold border-b border-[#2D5F3F] pb-1 hover:gap-3 transition-all"
            >
              Read full story <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* SEASON INFO – BENTO */}
      <section className="relative py-24 md:py-32 bg-[#EFE9D9] border-t border-black/10" data-testid="season-info-section">
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading
            eyebrow="2026 Tackle Football Season"
            title="Season Information"
            description="Cawston Titans plays in the Mount Baldy Conference. Fees cover uniforms and registration — earn your jersey."
          />

          <div className="grid grid-cols-12 gap-4 md:gap-6">
            <BentoCard className="col-span-12 md:col-span-5" icon={Users} label="Divisions" value="8U – 13U" sub="Youth tackle football" testid="info-ages" />
            <BentoCard className="col-span-6 md:col-span-4" icon={DollarSign} label="All-In Fee" value="$100" sub="Uniform + registration" testid="info-cost" />
            <BentoCard className="col-span-6 md:col-span-3" icon={Shirt} label="Includes" value="Uniform" sub="Provided with fee" testid="info-includes" />
            <BentoCard className="col-span-6 md:col-span-3" icon={Calendar} label="Practice" value="Jul 20" sub="Begins 2026" testid="info-start" />
            <BentoCard className="col-span-6 md:col-span-4" icon={ShieldCheck} label="Conference" value="Mt Baldy" sub="Mount Baldy Conference" testid="info-conference" />
            <BentoCard className="col-span-12 md:col-span-5" icon={MapPin} label="Practice Field" value="Rancho Viejo MS" sub="985 Cawston Ave N · Hemet, CA 92545" testid="info-location" />
          </div>

          {/* Documents row */}
          <div className="mt-10 bg-white border border-black/10 p-8 md:p-12" data-testid="documents-block">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="font-display text-2xl font-black uppercase text-[#0A0A0A]">Required Documents</h3>
                <p className="mt-2 text-black/65 text-sm">Bring to your first practice.</p>
              </div>
              <ul className="md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {["2026 Sports Physical", "Copy of Medical Insurance", "Birth Certificate"].map((doc, i) => (
                  <li key={doc} className="border-l-2 border-[#2D5F3F] pl-4">
                    <div className="font-display text-xs uppercase tracking-[0.2em] text-[#2D5F3F] font-bold">Doc 0{i + 1}</div>
                    <div className="text-[#0A0A0A] font-semibold mt-1">{doc}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/register"
              data-testid="season-cta-register"
              className="inline-flex items-center gap-3 bg-[#2D5F3F] hover:bg-[#1B4332] text-[#FDFBF7] font-display uppercase tracking-wider font-extrabold px-10 py-5 text-lg shadow-ink-offset hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all"
            >
              Register Today <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href="mailto:Cawstontitans@gmail.com"
              data-testid="season-cta-email"
              className="inline-flex items-center gap-3 border-2 border-[#2D5F3F] text-[#2D5F3F] hover:bg-[#2D5F3F] hover:text-[#FDFBF7] font-display uppercase tracking-wider font-bold px-8 py-5 text-base transition-colors"
            >
              Cawstontitans@gmail.com
            </a>
          </div>
        </div>
      </section>

      {/* MT BALDY CONFERENCE CHAPTERS */}
      <section className="bg-[#EFE9D9] py-24 md:py-28 border-t border-black/10" data-testid="chapters-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center mb-12">
            <div className="lg:col-span-3 flex justify-center lg:justify-start">
              <div className="bg-black p-4 inline-block shadow-ink-offset">
                <img
                  src="/assets/mt-baldy-logo.jpg"
                  alt="Mt. Baldy Pop Warner Conference"
                  data-testid="mt-baldy-logo"
                  className="w-44 md:w-52 h-auto object-contain"
                  draggable="false"
                />
              </div>
            </div>
            <div className="lg:col-span-9">
              <SectionHeading
                eyebrow="Mt. Baldy Pop Warner Conference"
                title="Member Chapters"
                description="The Titans compete alongside 20 chapters across Southern California. We rep Hemet."
              />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4" data-testid="chapters-grid">
            {[
              "Apple Valley", "Banning", "Charter Oak", "Chino Hills", "Corona (Tri-City)",
              "Fontana", "Hemet", "Jurupa", "La Verne", "Moreno Valley",
              "Ontario", "Perris", "Rancho", "Redlands", "Rialto",
              "Rosemead", "San Bernardino", "Upland", "West Covina", "Yucaipa",
            ].map((c) => {
              const isHome = c === "Hemet";
              return (
                <div
                  key={c}
                  data-testid={`chapter-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className={`relative px-4 py-4 md:py-5 font-display uppercase tracking-wide font-bold text-sm md:text-base transition-all ${
                    isHome
                      ? "bg-[#2D5F3F] text-[#FDFBF7] border-2 border-[#2D5F3F] shadow-ink-offset"
                      : "bg-white text-[#0A0A0A] border border-black/10 hover:border-[#2D5F3F]/60 hover:-translate-y-0.5"
                  }`}
                >
                  {isHome && (
                    <span className="absolute -top-2 right-3 bg-[#FDFBF7] text-[#2D5F3F] px-2 py-0.5 text-[9px] tracking-[0.25em] font-extrabold">
                      Home
                    </span>
                  )}
                  {c}
                </div>
              );
            })}
          </div>
        </div>
      </section>


      {/* OTHER SPORTS */}
      <section className="bg-[#F5EFE0] py-24 border-t border-black/10" data-testid="other-sports-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading eyebrow="Coming Soon" title="More Than Football" description="Cheer, basketball, and track & field on the horizon. Stay close." />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: "Cheer", path: "/cheer" },
              { name: "Basketball", path: "/basketball" },
              { name: "Track & Field", path: "/track-and-field" },
            ].map((s) => (
              <Link
                key={s.name}
                to={s.path}
                data-testid={`other-sport-${s.name.toLowerCase().replace(/\s+/g, "-").replace("&", "and")}`}
                className="group block bg-white border border-black/10 p-8 hover:border-[#2D5F3F]/60 hover:-translate-y-1 transition-all"
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-3xl font-black uppercase text-[#0A0A0A] group-hover:text-[#2D5F3F] transition-colors">{s.name}</h3>
                  <ArrowRight className="w-5 h-5 text-black/50 group-hover:text-[#2D5F3F] group-hover:translate-x-1 transition-all" />
                </div>
                <div className="mt-4 inline-block text-xs font-display uppercase tracking-[0.3em] text-[#2D5F3F] font-bold">Coming Soon</div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function BentoCard({ icon: Icon, label, value, sub, className = "", testid }) {
  return (
    <div
      data-testid={testid}
      className={`relative bg-white border border-black/10 p-6 md:p-8 hover:border-[#2D5F3F]/60 hover:-translate-y-1 transition-all group ${className}`}
    >
      <div className="flex items-start justify-between">
        <div className="font-display text-xs uppercase tracking-[0.3em] text-[#2D5F3F] font-bold">{label}</div>
        <Icon className="w-5 h-5 text-black/50 group-hover:text-[#2D5F3F] transition-colors" />
      </div>
      <div className="mt-6 font-display text-3xl sm:text-4xl md:text-5xl font-black uppercase text-[#0A0A0A] leading-none">{value}</div>
      {sub && <div className="mt-3 text-sm text-black/65">{sub}</div>}
    </div>
  );
}
